import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import {
  getDataAPI,
  postDataAPI,
  deleteDataAPI,
} from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import CourseCascade from "./CourseCascade";

/**
 * LMS - Exam setup (Council/Admin + Faculty).
 *
 * Define an exam for a course. A NEW exam can be created for SEVERAL subjects at
 * once (tick them) - one exam per subject, each titled "<title> - <subject>".
 * For a one-time (course_wise) course you can also mark the exam "auto": it has no
 * fixed date and opens for each student once their course duration completes.
 */
export default function Exams() {
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("adminToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [subjects, setSubjects] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [courseObj, setCourseObj] = useState(null);
  const [exams, setExams] = useState([]);

  const blank = {
    id: null, subject_id: "", subject_ids: [], auto: false, title: "", exam_date: "",
    duration_minutes: 30, total_questions: 20, marks_per_question: 1, passing_percent: 40,
  };
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(blank);

  // Keyed ONLY off exam_type. course_type "1" is not reliable (some yearly
  // courses also use it), and the "auto" exam is for one-time courses only.
  const isCourseWise = courseObj && courseObj.exam_type === "course_wise";

  const onCourse = async (cid, obj) => {
    setCourseId(cid);
    setCourseObj(obj || null);
    setSubjects([]);
    setExams([]);
    if (!cid) return;
    try {
      const [subs, ex] = await Promise.all([
        dispatch(getDataAPI(`admin/lms/content/subjects/${cid}`, token())),
        dispatch(getDataAPI(`admin/lms/exams?course_id=${cid}`, token())),
      ]);
      setSubjects(subs.data.data || []);
      setExams(ex.data.data || []);
    } catch (err) {
      alert({ error: "Could not load exams." });
    }
  };

  const reloadExams = async () => {
    const ex = await dispatch(getDataAPI(`admin/lms/exams?course_id=${courseId}`, token()));
    setExams(ex.data.data || []);
  };

  const openAdd = () => {
    setForm(blank);
    setShow(true);
  };
  const openEdit = (x) => {
    setForm({
      id: x.id, subject_id: x.subject_id || "", subject_ids: [], auto: Number(x.auto_duration) === 1,
      title: x.title, exam_date: x.exam_date || "", duration_minutes: x.duration_minutes,
      total_questions: x.total_questions, marks_per_question: x.marks_per_question,
      passing_percent: x.passing_percent,
    });
    setShow(true);
  };

  const toggleSubject = (id) => {
    const sid = Number(id);
    setForm((p) => ({
      ...p,
      subject_ids: p.subject_ids.includes(sid)
        ? p.subject_ids.filter((x) => x !== sid)
        : [...p.subject_ids, sid],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        course_id: courseId,
        auto_duration: isCourseWise && form.auto ? 1 : 0,
      };
      if (form.id) {
        body.subject_id = form.subject_id || 0;
      } else {
        // new: multi-select subjects (empty = whole course)
        body.subject_ids = form.subject_ids;
        body.subject_id = 0;
      }
      const res = await dispatch(postDataAPI("admin/lms/exam", body, token()));
      alert({ success: res.data.message || "Exam saved." });
      setShow(false);
      reloadExams();
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not save exam." });
    }
  };

  const remove = async (x) => {
    if (!window.confirm(`Delete exam "${x.title}"?`)) return;
    try {
      await dispatch(deleteDataAPI(`admin/lms/exam/${x.id}`, token()));
      alert({ success: "Exam removed." });
      reloadExams();
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not delete." });
    }
  };

  return (
    <div className="container-fluid" style={{ padding: 20 }}>
      <h4 className="mb-3">Exams</h4>

      <CourseCascade onCourse={(id, obj) => onCourse(id, obj)} />

      {courseId ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 style={{ margin: 0 }}>Exam list</h5>
            <Button size="sm" variant="primary" onClick={openAdd}>+ Add Exam</Button>
          </div>
          <table className="table table-bordered bg-white">
            <thead>
              <tr>
                <th>Title</th>
                <th>Subject</th>
                <th>Date</th>
                <th>Duration</th>
                <th>Questions (pool)</th>
                <th>Pass %</th>
                <th style={{ width: 150 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {exams.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted">No exams yet.</td></tr>
              ) : (
                exams.map((x) => (
                  <tr key={x.id}>
                    <td>{x.title}</td>
                    <td>{x.subject_name || (x.subject_id ? x.subject_id : "Whole course")}</td>
                    <td>{Number(x.auto_duration) === 1 ? <span className="badge badge-info">Auto (after course duration)</span> : (x.exam_date || "—")}</td>
                    <td>{x.duration_minutes} min</td>
                    <td>
                      {x.total_questions}
                      {typeof x.pool !== "undefined" && (
                        <span className={x.pool < x.total_questions ? "text-danger" : "text-muted"}>
                          {" "}/ {x.pool} in bank
                        </span>
                      )}
                    </td>
                    <td>{x.passing_percent}%</td>
                    <td>
                      <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => openEdit(x)}>Edit</Button>{" "}
                      <Button size="sm" variant="outline-danger" onClick={() => remove(x)}>Delete</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      ) : (
        <p className="text-muted">Choose a course to set up its exams.</p>
      )}

      <Modal show={show} onHide={() => setShow(false)}>
        <form onSubmit={save}>
          <Modal.Header closeButton>
            <Modal.Title>{form.id ? "Edit Exam" : "Add Exam"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label>Title</label>
              <input className="form-control" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </div>

            {form.id ? (
              <div className="form-group">
                <label>Subject (leave "Whole course" for a course-wide exam)</label>
                <select className="form-control" value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}>
                  <option value="">Whole course</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} {s.code ? `(${s.code})` : ""}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="form-group">
                <label>Subjects (tick one or more — one exam is created per subject; tick none for a whole-course exam)</label>
                <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #ddd", borderRadius: 4, padding: 10 }}>
                  {subjects.length === 0 ? (
                    <span className="text-muted">No subjects in this course — a whole-course exam will be created.</span>
                  ) : (
                    subjects.map((s) => (
                      <label key={s.id} style={{ display: "block", marginBottom: 6, fontWeight: 400 }}>
                        <input
                          type="checkbox"
                          checked={form.subject_ids.includes(Number(s.id))}
                          onChange={() => toggleSubject(s.id)}
                          style={{ marginRight: 8 }}
                        />
                        {s.name} {s.code ? `(${s.code})` : ""}
                      </label>
                    ))
                  )}
                </div>
                <small className="text-muted">Selected: {form.subject_ids.length || "none (whole course)"}</small>
              </div>
            )}

            {isCourseWise && (
              <div className="form-group">
                <label style={{ fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={form.auto}
                    onChange={(e) => setForm({ ...form, auto: e.target.checked })}
                    style={{ marginRight: 8 }}
                  />
                  Auto (one-time course): no fixed date — opens for each student after their course
                  duration completes and fees are paid.
                </label>
              </div>
            )}

            {!form.auto && (
              <div className="form-group">
                <label>Exam date</label>
                <input type="date" className="form-control" value={form.exam_date || ""} onChange={(e) => setForm({ ...form, exam_date: e.target.value })} />
              </div>
            )}

            <div className="form-row">
              <div className="form-group col">
                <label>Duration (minutes)</label>
                <input type="number" min="1" className="form-control" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} required />
              </div>
              <div className="form-group col">
                <label>Total questions</label>
                <input type="number" min="1" className="form-control" value={form.total_questions} onChange={(e) => setForm({ ...form, total_questions: e.target.value })} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col">
                <label>Marks / question</label>
                <input type="number" min="1" className="form-control" value={form.marks_per_question} onChange={(e) => setForm({ ...form, marks_per_question: e.target.value })} />
              </div>
              <div className="form-group col">
                <label>Passing %</label>
                <input type="number" min="0" max="100" className="form-control" value={form.passing_percent} onChange={(e) => setForm({ ...form, passing_percent: e.target.value })} />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
